import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { clearUserProfile } from '../../../../redux/actions/user';
import { clearBugsnagUser } from '../../../../redux/actions/bugsnag';
import Alerts from '../../../../prototypes/Alerts';
import EtelAPI from '../../../../api/EtelAPI';
import Overlay from '../../../../presentation/Overlay';
import DeleteItemDialog from '../../../../presentation/DeleteItemDialog';
import NoSearchResults from '../../../../presentation/NoSearchResults';
import PageSizeSelector from '../../../../presentation/PageSizeSelector';
import PagePositionLabel from '../../../../presentation/PagePositionLabel';
import Pagination from '../../../../presentation/Pagination';

import ReactGrid,
{
  ReactGridHeader,
  ReactGridColumn,
  ReactGridBody,
  ReactGridRowTemplate,
  ReactGridIconCell,
  ReactGridTextboxCell,
  ReactGridDropDownCell,
  ReactGridReadOnlyCell,
  ReactGridTextAreaCell,
  ReactGridNoRowsTemplate
} from '../../../../presentation/ReactGrid';

class WholesaleSIPInfo extends Component {
  constructor( props ) {
    super( props );

    this.state = {
      row: props.row === undefined ? {} : props.row,
      newItem: [],
      sipInfoToDelete: {},
      dataSource: [],
      codecs: [],
      showNewItem: false,
      showDeleteDialog: false,
      gridHeight: '570px',
      sipInfoFilter: props.row.SIP_INFO_NAME === undefined ? '' : props.row.SIP_INFO_NAME,
      pageSize: 25,
      pageIndex: 0,
    };

    this.onHandleChangeReactGrid = this.onHandleChangeReactGrid.bind( this );
    this.onHandleChange = this.onHandleChange.bind( this );
    this.onHandleKeyDown = this.onHandleKeyDown.bind( this );
    this.onHandleClickDelete = this.onHandleClickDelete.bind( this );
    this.onHandleClickInsert = this.onHandleClickInsert.bind( this );
    this.onHandleClickUpdate = this.onHandleClickUpdate.bind( this );
    this.onHandleClickCancel = this.onHandleClickCancel.bind( this );
    this.onHandleClickConfirmDelete = this.onHandleClickConfirmDelete.bind( this );
    this.onHandleClickNewSIPInfo = this.onHandleClickNewSIPInfo.bind( this );
    this.onHandleClickIcon = this.onHandleClickIcon.bind( this );
    this.onHandleClickSearch = this.onHandleClickSearch.bind( this );
    this.onPageSizeChanged = this.onPageSizeChanged.bind( this );
    this.onPageChanged = this.onPageChanged.bind( this );

    this.onHandleException = this.onHandleException.bind( this );

    this.title = 'Wholesale SIP Info';
  }

  /****************************************************************************
   * STATE / LIFECYCLE METHODS
   ***************************************************************************/

  componentDidMount() {
    this.alert = new Alerts( this.props.bugsnag, this.props.dispatch );

    this.getData();

    this.setNewItem();
  }

  /****************************************************************************
   * API CALLS
   ***************************************************************************/

  getData() {
    const row = { ...this.state.row };
    const sipInfoParams = [
      { COMPANY_ID: row.Company_ID },
      { SIP_INFO_NAME: this.state.sipInfoFilter }
    ];

    const all = () => Promise.all( [
      this.getSipInfo( sipInfoParams ),
      this.getCodecs( [] )
    ] ).then( ( [sipInfoResults, codecResults] ) => {
      this.setState( {
        dataSource: sipInfoResults,
        codecs: codecResults
      } );
    } ).catch( ( exception ) => {
      this.onHandleException( exception );
    } );

    all();
  }

  getSipInfo( params ) {
    return new Promise( ( resolve, reject ) => {
      const api = new EtelAPI( 'wholesale/sipinfo' );

      api.get( params ).then( ( sipInfoResults ) => {
        resolve( sipInfoResults );
      } ).catch( ( exception ) => {
        reject( exception );
      } );
    } );
  }

  updateSipInfo( params ) {
    return new Promise( ( resolve, reject ) => {
      const api = new EtelAPI( 'wholesale/sipinfo' );

      api.update( params ).then( ( sipInfoResults ) => {
        resolve( sipInfoResults );
      } ).catch( ( exception ) => {
        reject( exception );
      } );
    } );
  }

  insertSipInfo( params ) {
    return new Promise( ( resolve, reject ) => {
      const api = new EtelAPI( 'wholesale/sipinfo' );

      api.insert( params ).then( ( sipInfoResults ) => {
        resolve( sipInfoResults );
      } ).catch( ( exception ) => {
        reject( exception );
      } );
    } );
  }

  deleteSipInfo( params ) {
    return new Promise( ( resolve, reject ) => {
      const api = new EtelAPI( 'wholesale/sipinfo' );

      api.delete( params ).then( ( sipInfoResults ) => {
        resolve( sipInfoResults );
      } ).catch( ( exception ) => {
        reject( exception );
      } );
    } );
  }

  getCodecs( params ) {
    return new Promise( ( resolve, reject ) => {
      const api = new EtelAPI( 'types/codec' );

      api.get( params ).then( ( codecResults ) => {
        resolve( codecResults );
      } ).catch( ( exception ) => {
        reject( exception );
      } );
    } );
  }

  /****************************************************************************
   * EXCEPTION HANDLING
   ***************************************************************************/

  doLogOff() {
    const { dispatch, auth, history } = this.props;
    auth.logout();
    dispatch( clearBugsnagUser() );
    dispatch( clearUserProfile() );
    history.push( '/login' );
  }

  onHandleException( e ) {
    const { dispatch, bugsnag } = this.props;
    const alerts = new Alerts( bugsnag, dispatch );
    const status = e.status === undefined ? 0 : e.status;
    if ( status === 410 ) {
      alerts.INFO( this.title, 'Your session has expired.', 5000 );
      this.doLogOff();
    } else if ( status === 500 ) {
      alerts.INFO( this.title, e.statusText, 5000 );
    } else {
      if ( e.statusText !== undefined ) {
        alerts.CRITICAL( this.title, e, false );
      } else {
        alerts.CRITICAL( this.title, e );
      }
    }
  }

  /****************************************************************************
   * HELPER METHODS
   ***************************************************************************/

  getCodecOptions() {
    return this.state.codecs.map( ( codec ) => {
      return {
        id: codec.CODEC_ID,
        text: codec.NUMBER
      };
    } );
  }

  setNewItem() {
    const newItem = [
      {
        SIP_INFO_ID: 0,
        SIP_INFO_NAME: '',
        COMPANY_ID: this.state.row.Company_ID,
        PRIMARY_ADDRESS: '',
        SECONDARY_ADDRESS: '',
        TERTIARY_ADDRESS: '',
        CODEC: '',
        DTMF: '',
        DIGIT_FORMAT: '',
        INFO_DIGIT: '',
        DEDICATED_BANDWIDTH: '',
        EDGE_DEVICE: '',
        CONCURRENT_CALLS: 0,
        MAX_CALLS_PER_SECOND: 0,
        NOTES: ''
      }
    ];

    this.setState( {
      newItem: newItem,
      showNewItem: false,
      gridHeight: '570px'
    } );
  }

  /****************************************************************************
  * EVENT HANDLING
  ****************************************************************************/

  /**
   * Handles the change event to any column/item in ReactGrid.
   *
   * ReactGrid passes args back as:
   * {
   *  column: "",
   *  rowIndex: 0,
   *  cellIndex: 1,
   *  oldvalue: "",
   *  newvalue: "",
   *  oldrow: {},
   *  newrow: {}
   * }
   * @param {Object} args
   */
  onHandleChangeReactGrid( args ) {
    const { rowIndex, column, newvalue, newrow } = args;
    const sipInfoID = newrow.SIP_INFO_ID;
    let dataSourceToUpdate = [];
    let rowToUpdate = {};
    let stateToUpdate = '';

    if ( sipInfoID === 0 ) {
      dataSourceToUpdate = [...this.state.newItem];
      stateToUpdate = 'newItem';
    } else {
      dataSourceToUpdate = [...this.state.dataSource];
      stateToUpdate = 'dataSource';
    }

    rowToUpdate = { ...dataSourceToUpdate[rowIndex] };
    rowToUpdate[column] = newvalue;
    dataSourceToUpdate[rowIndex] = rowToUpdate;

    this.setState( {
      [stateToUpdate]: dataSourceToUpdate
    } );
  }

  onHandleChange( e ) {
    this.setState( {
      [e.target.name]: e.target.value
    } );
  }

  onHandleKeyDown( e ) {
    if ( e.key === 'Enter' ) {
      this.onHandleClickSearch();
    }
  }

  onHandleClickSearch( e ) {
    const params = [
      { COMPANY_ID: this.state.row.Company_ID },
      { SIP_INFO_NAME: this.state.sipInfoFilter }
    ];

    this.getSipInfo( params ).then( ( sipInfoResults ) => {
      this.setState( {
        dataSource: sipInfoResults
      } );
    } ).catch( ( exception ) => {
      this.onHandleException( exception );
    } );
  }

  onHandleClickInsert( args ) {
    const { rowIndex } = args;
    const sipInfoToInsert = { ...this.state.newItem[rowIndex] };
    const params = {
      SIP_INFO_ID: sipInfoToInsert.SIP_INFO_ID,
      SIP_INFO_NAME: sipInfoToInsert.SIP_INFO_NAME,
      COMPANY_ID: sipInfoToInsert.COMPANY_ID,
      PRIMARY_ADDRESS: sipInfoToInsert.PRIMARY_ADDRESS,
      SECONDARY_ADDRESS: sipInfoToInsert.SECONDARY_ADDRESS,
      TERTIARY_ADDRESS: sipInfoToInsert.TERTIARY_ADDRESS,
      CODEC: sipInfoToInsert.CODEC,
      DTMF: sipInfoToInsert.DTMF,
      DIGIT_FORMAT: sipInfoToInsert.DIGIT_FORMAT,
      INFO_DIGIT: sipInfoToInsert.INFO_DIGIT,
      DEDICATED_BANDWIDTH: sipInfoToInsert.DEDICATED_BANDWIDTH,
      EDGE_DEVICE: sipInfoToInsert.EDGE_DEVICE,
      CONCURRENT_CALLS: sipInfoToInsert.CONCURRENT_CALLS,
      MAX_CALLS_PER_SECOND: sipInfoToInsert.MAX_CALLS_PER_SECOND,
      NOTES: sipInfoToInsert.NOTES
    };

    this.insertSipInfo( params ).then( ( result ) => {
      const updatedDataSource = [...this.state.dataSource];
      updatedDataSource.unshift( result.data[0] );

      this.setState( {
        dataSource: updatedDataSource
      }, () => {
        this.setNewItem();
      } );

      this.alert.SUCCESS( this.title, `Successfully inserted ${this.title}.`, 2500 );
    } ).catch( ( exception ) => {
      this.onHandleException( exception );
    } );
  }

  onHandleClickUpdate( args ) {
    const { rowIndex } = args;
    const sipInfoToUpdate = { ...this.state.dataSource[rowIndex] };
    const params = {
      SIP_INFO_ID: sipInfoToUpdate.SIP_INFO_ID,
      SIP_INFO_NAME: sipInfoToUpdate.SIP_INFO_NAME,
      COMPANY_ID: sipInfoToUpdate.COMPANY_ID,
      PRIMARY_ADDRESS: sipInfoToUpdate.PRIMARY_ADDRESS,
      SECONDARY_ADDRESS: sipInfoToUpdate.SECONDARY_ADDRESS,
      TERTIARY_ADDRESS: sipInfoToUpdate.TERTIARY_ADDRESS,
      CODEC: sipInfoToUpdate.CODEC,
      DTMF: sipInfoToUpdate.DTMF,
      DIGIT_FORMAT: sipInfoToUpdate.DIGIT_FORMAT,
      INFO_DIGIT: sipInfoToUpdate.INFO_DIGIT,
      DEDICATED_BANDWIDTH: sipInfoToUpdate.DEDICATED_BANDWIDTH,
      EDGE_DEVICE: sipInfoToUpdate.EDGE_DEVICE,
      CONCURRENT_CALLS: sipInfoToUpdate.CONCURRENT_CALLS,
      MAX_CALLS_PER_SECOND: sipInfoToUpdate.MAX_CALLS_PER_SECOND,
      NOTES: sipInfoToUpdate.NOTES
    };

    this.updateSipInfo( params ).then( ( result ) => {
      const args = {
        row: result.data[0]
      };

      this.alert.SUCCESS( this.title, `Successfully updated ${this.title}.`, 2500 );

      this.props.onSave( args );
    } ).catch( ( exception ) => {
      this.onHandleException( exception );
    } );
  }

  onHandleClickDelete( args ) {
    if ( args.row.SIP_INFO_ID === 0 ) {
      this.setNewItem();

      return;
    }

    const { rowIndex } = args;
    const sipInfoToDelete = { ...this.state.dataSource[rowIndex] };

    this.setState( {
      showDeleteDialog: true,
      sipInfoToDelete: sipInfoToDelete
    } );
  }

  onHandleClickCancel( e ) {
    this.setState( {
      showDeleteDialog: false
    } );
  }

  onHandleClickConfirmDelete( e ) {
    const sipInfoID = this.state.sipInfoToDelete.SIP_INFO_ID;

    this.deleteSipInfo( sipInfoID ).then( ( result ) => {
      const updatedDataSource = this.state.dataSource.filter( ( sipInfo ) => {
        return sipInfo.SIP_INFO_ID !== sipInfoID;
      } );

      this.setState( {
        dataSource: updatedDataSource,
        showDeleteDialog: false
      } );

      this.alert.SUCCESS( this.title, `Successfully deleted ${this.title}.`, 2500 );
    } ).catch( ( exception ) => {
      this.onHandleException( exception );
    } );
  }

  onHandleClickNewSIPInfo( e ) {
    if ( this.state.showNewItem ) {
      return;
    }
    
    this.setState( {
      showNewItem: true,
      gridHeight: '400px'
    } );
  }

  onHandleClickIcon( args ) {
    const { iconKey } = args;

    if ( iconKey === 'update' ) {
      this.onHandleClickUpdate( args );
    } else if ( iconKey === 'insert' ) {
      this.onHandleClickInsert( args );
    } else if ( iconKey === 'delete' ) {
      this.onHandleClickDelete( args );
    }
  }

  onPageSizeChanged( pageSize ) {
    this.setState( {
      pageSize: pageSize
    } );
  }

  onPageChanged( page, e ) {
    this.setState( {
      pageIndex: page.index
    } );
  }

  /****************************************************************************
   * STYLES
   ***************************************************************************/

  getDialogContentStyle() {
    return {
      display: 'grid',
      gridTemplateRows: `${this.state.gridHeight} 50px`,
      width: '100%',
      height: this.state.gridHeight + 50 + 'px',
      marginTop: this.state.showNewItem ? '50px' : 0
    };
  }

  /****************************************************************************
   * RENDERS
   ***************************************************************************/

  renderPageSizeSelector() {
    let pageSizeSelectorControl = null;

    if ( this.state.dataSource !== undefined ) {
      if ( this.state.dataSource.length > 0 ) {
        if ( this.state.dataSource.length >= this.state.pageSize ) {
          pageSizeSelectorControl = (
            <PageSizeSelector options={this.getPageSizeOptions()} onPageSizeChanged={this.onPageSizeChanged} />
          );
        }
      }
    }

    return pageSizeSelectorControl;
  }

  renderPagination() {
    const pageSize = this.state.pageSize;
    const pageStart = ( parseInt( this.state.pageIndex, 10 ) * parseInt( pageSize, 10 ) ) + 1;
    const pageEnd = pageStart + parseInt( pageSize, 10 ) - 1;
    const pageTotal = this.state.dataSource === undefined ? 0 : this.state.dataSource.length;

    return (
      <span className='dialog-footer-content-pagination'>
        <span className='dialog-footer-content-pagination-cell-1'>
          <PagePositionLabel pagesize={pageSize} pagestart={pageStart} pageend={pageEnd} pagetotal={pageTotal} />
        </span>
        <span className='dialog-footer-content-pagination-cell-2'>
        </span>
        <span className='dialog-footer-content-pagination-cell-3'>
          <Pagination pageindex={this.state.pageIndex} totalitems={pageTotal} maxitemsperpage={this.state.pageSize} maxpages={6} onPageChanged={this.onPageChanged} />
        </span>
      </span>
    );
  }

  renderNewItem() {
    const codecOptions = this.getCodecOptions();

    return (
      <ReactGrid
        tagName='grid'
        dataSource={this.state.newItem}
        height='auto'
        width='auto'
        paging={true}
        pageSize={this.state.pageSize}
        pageIndex={this.state.pageIndex}
        onCellChanged={this.onHandleChangeReactGrid}
        onIconClicked={this.onHandleClickIcon}
      >
        <ReactGridHeader
          tagName='header'
        >
          <ReactGridColumn
            tagName='column'
            caption='SIP Info Name'
            width='150px'
            sorted='SIP_INFO_NAME'
            dataType='string'
            index={0}
          />
          <ReactGridColumn
            tagName='column'
            caption='1st IP Address'
            width='120px'
            sorted='PRIMARY_ADDRESS'
            dataType='string'
            index={1}
          />
          <ReactGridColumn
            tagName='column'
            caption='2nd IP Address'
            width='120px'
            sorted='SECONDARY_ADDRESS'
            dataType='string'
            index={2}
          />
          <ReactGridColumn
            tagName='column'
            caption='3rd IP Address'
            width='120px'
            sorted='TERTIARY_ADDRESS'
            dataType='string'
            index={3}
          />
          <ReactGridColumn
            tagName='column'
            caption='Codec'
            width='100px'
            sorted='CODEC'
            dataType='string'
            index={4}
          />
          <ReactGridColumn
            tagName='column'
            caption='DTMF'
            width='100px'
            sorted='DTMF'
            dataType='string'
            index={5}
          />
          <ReactGridColumn
            tagName='column'
            caption='Info Digits'
            width='70px'
            sorted='INFO_DIGIT'
            dataType='string'
            index={6}
          />
          <ReactGridColumn
            tagName='column'
            caption='Dedicated Bandwidth'
            width='100px'
            sorted='DEDICATED_BANDWIDTH'
            dataType='string'
            index={7}
          />
          <ReactGridColumn
            tagName='column'
            caption='Edge Device'
            width='120px'
            sorted='EDGE_DEVICE'
            dataType='string'
            index={8}
          />
          <ReactGridColumn
            tagName='column'
            caption='Concurrent Calls'
            width='100px'
            sorted='CONCURRENT_CALLS'
            dataType='integer'
            index={9}
          />
          <ReactGridColumn
            tagName='column'
            caption='Max Calls/Second'
            width='100px'
            sorted='MAX_CALLS_PER_SECOND'
            dataType='integer'
            index={10}
          />
          <ReactGridColumn
            tagName='column'
            caption='Notes'
            width='150px'
            sorted='NOTES'
            dataType='string'
            index={11}
          />
          <ReactGridIconCell
            tagName='column'
            width='50px'
            icon='icon-fontello-floppy-1'
            tooltip='Insert SIP Info'
            index={12}
          />
          <ReactGridIconCell
            tagName='column'
            width='50px'
            icon='icon-fontello-trash-8'
            tooltip='Delete SIP Info'
            index={13}
          />
          <ReactGridColumn
            index={14}
            tagName='column'
            caption=''
            width='auto'
          />
        </ReactGridHeader>
        <ReactGridBody tagName="body">
          <ReactGridRowTemplate tagName='rowTemplate'>
            <ReactGridTextAreaCell
              tagName='textAreaCell'
              column='SIP_INFO_NAME'
              width='150px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='PRIMARY_ADDRESS'
              width='120px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='SECONDARY_ADDRESS'
              width='120px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='TERTIARY_ADDRESS'
              width='120px'
              align='center'
            />
            <ReactGridDropDownCell
              tagName='dropDownCell'
              column='CODEC'
              width='100px'
              align='center'
              options={codecOptions}
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='DTMF'
              width='100px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='INFO_DIGIT'
              width='70px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='DEDICATED_BANDWIDTH'
              width='100px'
              align='center'
            />
            <ReactGridTextAreaCell
              tagName='textAreaCell'
              column='EDGE_DEVICE'
              width='120px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='CONCURRENT_CALLS'
              width='100px'
              align='center'
            />
            <ReactGridTextboxCell
              tagName='textboxCell'
              column='MAX_CALLS_PER_SECOND'
              width='100px'
              align='center'
            />
            <ReactGridTextAreaCell
              tagName='textAreaCell'
              column='NOTES'
              width='150px'
              align='center'
            />
            <ReactGridIconCell
              tagName='iconCell'
              width='50px'
              align='center'
              icon='icon-fontello-floppy-1'
              iconKey='insert'
            />
            <ReactGridIconCell
              tagName='iconCell'
              width='50px'
              align='center'
              icon='icon-fontello-trash-8'
              iconKey='delete'
              iconColor='#BE000E'
            />
            <ReactGridReadOnlyCell
              tagName='readOnlyCell'
              column=''
              width='auto'
            />
          </ReactGridRowTemplate>
        </ReactGridBody>
      </ReactGrid>
    );
  }

  render() {
    const codecOptions = this.getCodecOptions();
    const showOverlay = this.state.showDeleteDialog;

    return (
      <span>
        <span className='wholesale-dialog-header-content'>
          <button
            className='dialog-save-button'
            style={{ width: '150px', height: '32px' }}
            onClick={this.onHandleClickNewSIPInfo}
          >
            New SIP Info
          </button>
          <span className='wholesale-dialog-search-content'>
            <label className='wholesale-dialog-search-label'>Search SIP Info Description (Use % as needed)</label>
            <input className='wholesale-dialog-search-input' name='sipInfoFilter' value={this.state.sipInfoFilter} type="text" onChange={this.onHandleChange} onKeyDown={this.onHandleKeyDown} />
            <button className='dialog-save-button' style={{ height: '32px', marginRight: 0 }} onClick={this.onHandleClickSearch}>Search</button>
          </span>
          {
            this.renderPageSizeSelector()
          }
        </span>
        {
          this.state.showNewItem && this.renderNewItem()
        }
        <span style={this.getDialogContentStyle()}>
          <ReactGrid
            tagName='grid'
            dataSource={this.state.dataSource}
            height='auto'
            width='auto'
            paging={true}
            pageSize={this.state.pageSize}
            pageIndex={this.state.pageIndex}
            onCellChanged={this.onHandleChangeReactGrid}
            onIconClicked={this.onHandleClickIcon}
          >
            <ReactGridHeader
              tagName='header'
            >
              <ReactGridColumn
                tagName='column'
                caption='SIP Info Name'
                width='150px'
                sorted='SIP_INFO_NAME'
                dataType='string'
                index={0}
              />
              <ReactGridColumn
                tagName='column'
                caption='1st IP Address'
                width='120px'
                sorted='PRIMARY_ADDRESS'
                dataType='string'
                index={1}
              />
              <ReactGridColumn
                tagName='column'
                caption='2nd IP Address'
                width='120px'
                sorted='SECONDARY_ADDRESS'
                dataType='string'
                index={2}
              />
              <ReactGridColumn
                tagName='column'
                caption='3rd IP Address'
                width='120px'
                sorted='TERTIARY_ADDRESS'
                dataType='string'
                index={3}
              />
              <ReactGridColumn
                tagName='column'
                caption='Codec'
                width='100px'
                sorted='CODEC'
                dataType='string'
                index={4}
              />
              <ReactGridColumn
                tagName='column'
                caption='DTMF'
                width='100px'
                sorted='DTMF'
                dataType='string'
                index={5}
              />
              <ReactGridColumn
                tagName='column'
                caption='Info Digits'
                width='70px'
                sorted='INFO_DIGIT'
                dataType='string'
                index={6}
              />
              <ReactGridColumn
                tagName='column'
                caption='Dedicated Bandwidth'
                width='100px'
                sorted='DEDICATED_BANDWIDTH'
                dataType='string'
                index={7}
              />
              <ReactGridColumn
                tagName='column'
                caption='Edge Device'
                width='120px'
                sorted='EDGE_DEVICE'
                dataType='string'
                index={8}
              />
              <ReactGridColumn
                tagName='column'
                caption='Concurrent Calls'
                width='100px'
                sorted='CONCURRENT_CALLS'
                dataType='integer'
                index={9}
              />
              <ReactGridColumn
                tagName='column'
                caption='Max Calls/Second'
                width='100px'
                sorted='MAX_CALLS_PER_SECOND'
                dataType='integer'
                index={10}
              />
              <ReactGridColumn
                tagName='column'
                caption='Notes'
                width='150px'
                sorted='NOTES'
                dataType='string'
                index={11}
              />
              <ReactGridIconCell
                tagName='column'
                width='50px'
                icon='icon-fontello-floppy-1'
                tooltip='Update SIP Info'
                index={12}
              />
              <ReactGridIconCell
                tagName='column'
                width='50px'
                icon='icon-fontello-trash-8'
                tooltip='Delete SIP Info'
                index={13}
              />
              <ReactGridColumn
                index={14}
                tagName='column'
                caption=''
                width='auto'
              />
            </ReactGridHeader>
            <ReactGridBody tagName="body">
              <ReactGridRowTemplate tagName='rowTemplate'>
                <ReactGridTextAreaCell
                  tagName='textAreaCell'
                  column='SIP_INFO_NAME'
                  width='150px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='PRIMARY_ADDRESS'
                  width='120px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='SECONDARY_ADDRESS'
                  width='120px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='TERTIARY_ADDRESS'
                  width='120px'
                  align='center'
                />
                <ReactGridDropDownCell
                  tagName='dropDownCell'
                  column='CODEC'
                  width='100px'
                  align='center'
                  options={codecOptions}
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='DTMF'
                  width='100px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='INFO_DIGIT'
                  width='70px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='DEDICATED_BANDWIDTH'
                  width='100px'
                  align='center'
                />
                <ReactGridTextAreaCell
                  tagName='textAreaCell'
                  column='EDGE_DEVICE'
                  width='120px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='CONCURRENT_CALLS'
                  width='100px'
                  align='center'
                />
                <ReactGridTextboxCell
                  tagName='textboxCell'
                  column='MAX_CALLS_PER_SECOND'
                  width='100px'
                  align='center'
                />
                <ReactGridTextAreaCell
                  tagName='textAreaCell'
                  column='NOTES'
                  width='150px'
                  align='center'
                />
                <ReactGridIconCell
                  tagName='iconCell'
                  width='50px'
                  align='center'
                  icon='icon-fontello-floppy-1'
                  iconKey='update'
                />
                <ReactGridIconCell
                  tagName='iconCell'
                  width='50px'
                  align='center'
                  icon='icon-fontello-trash-8'
                  iconKey='delete'
                  iconColor='#BE000E'
                />
                <ReactGridReadOnlyCell
                  tagName='readOnlyCell'
                  column=''
                  width='auto'
                />
              </ReactGridRowTemplate>
              <ReactGridNoRowsTemplate tagName='noRowTemplate' height='50px'>
                <NoSearchResults caption="There is no SIP info." />
              </ReactGridNoRowsTemplate>
            </ReactGridBody>
          </ReactGrid>
          {
            this.renderPagination()
          }
        </span>
        {
          showOverlay && <Overlay show={showOverlay} />
        }
        {
          this.state.showDeleteDialog &&
          <DeleteItemDialog
            show={this.state.showDeleteDialog}
            layout={{ newbutton: { caption: this.title } }}
            onCancel={this.onHandleClickCancel}
            onConfirm={this.onHandleClickConfirmDelete}
          />
        }
      </span>
    );
  }
};

export default withRouter(
  connect(
    ( store ) => {
      return {
        auth: store.auth.auth,
        bugsnag: store.bugsnag.Bugsnag,
        user: store.user.user
      };
    }
  )( WholesaleSIPInfo ) );


// WEBPACK FOOTER //
// ./src/content/wholesale/sipinfo/components/WholesaleSIPInfo.js